import { getApolloSessionDetails } from '../../../features/apolloSession/apolloSession.selectors';
import {
  type DispatchReturnPromise,
  useAppSelector,
  useAppDispatch,
} from '../../useReduxToolkit';
import { useSession } from '../../useSession';
import { type TransitionFunc } from '../useCall.types';
import * as realtime from '../../../features/realtime/realtime.slice';
import {
  RealtimeMessageType,
  RealtimeRole,
} from '../../../features/realtime/realtime.types';
import { useCallParticipants } from '../../useCallParticipants';
import { CallTransition } from '../../../features/call/call.types';
import { useRestartLocalMedia } from '../../useLocalMedia';
import { useRef } from 'react';
import { type RealtimeEventCbCancel } from '@doxyme/realtime';

import { getLogger } from '../../../libs/logger';
import { subscribeToMessage } from '../../../features/realtime/realtime.subscribe';
const logger = getLogger('useCallStateRestarting');

export const useCallStateRestarting = (
  transition: TransitionFunc,
): (() => Promise<void>) => {
  const { role } = useAppSelector(getApolloSessionDetails);

  const { disconnect: disconnectSession, reconnect: reconnectSession } =
    useSession();
  const { restartMedia } = useRestartLocalMedia();
  const { callParticipants: participants } = useCallParticipants();
  const callRestartIsReadyUnsubscribeRef = useRef<RealtimeEventCbCancel>(
    () => undefined,
  );

  const dispatch = useAppDispatch();

  const inviteMemberToRestart = async (id: string) => {
    logger.debug('[restartSession/inviteMemberToRestart] Debug:', {
      participantId: id,
    });

    try {
      await dispatch(
        realtime.sendMessage({
          id,
          message: {
            messageType: RealtimeMessageType.CallRestart,
          },
        }),
      ).unwrap();
    } catch (error) {
      logger.error('[restartSession/inviteMemberToRestart] Error:', {
        participantId: id,
        error,
      });
    }
  };

  const notifyThatIAmReady = async (id: string): DispatchReturnPromise => {
    logger.debug('[restartSession/notifyThatIAmReady] Debug:', {
      participantId: id,
    });
    return dispatch(
      realtime.sendMessage({
        id,
        message: {
          messageType: RealtimeMessageType.CallRestartIsReady,
        },
      }),
    );
  };

  const waitWithCancel = (
    ms: number,
  ): { cancel: () => void; wait: Promise<void> } => {
    let timeout: ReturnType<typeof setTimeout> | undefined = undefined;

    return {
      cancel: () => (timeout !== undefined ? clearTimeout(timeout) : undefined),
      wait: new Promise((resolve) => {
        timeout = setTimeout(resolve, ms);
      }),
    };
  };

  /**
   * Checks that all participants are disconnected and ready to start the call from sсratch.
   * We need it to avoid next situation:
   * - Provider: send patient to initiate invite
   * - Provider: disconnected
   * - Provider: restarted media
   * - Provider: reconnected
   * - Provider: got patient already in call (because patient has not even received the restart message  yet for example or had some issues with disconnect)
   * - Provider: patient joined(here we treat it as a successfully restarted call even if the patient has not started it yet)
   * - Patient: got a message to restart
   * - Patient: disconnected
   * - Provider: Got patient Left and ends the call. (@bug: https://doxyme.atlassian.net/browse/CS-10699)
   * @returns {Promise<void>}
   */
  const waitForOthersToBeReady = async (ids: string[]): Promise<void> => {
    return new Promise<void>((resolve: () => void) => {
      let unresolvedIds = ids;

      /**
       * Automatically resolve by timeout
       */
      const timeout = 10000;
      const { wait, cancel: cancelTimeout } = waitWithCancel(timeout);
      wait
        .then(() => {
          logger.warn(
            '[restartSession/waitForOthersToBeReady] Warning: Timeout',
            {
              unresolvedIds,
              timeout,
            },
          );
          resolve();
          return null;
        })
        .catch((error) => {
          logger.error('[restartSession/waitForOthersToBeReady]', error);
          throw error;
        });

      logger.debug('[restartSession/waitForOthersToBeReady] Debug: Subscribe', {
        unresolvedIds,
        timeout,
      });

      /**
       * Unsubscribe from the previous onMessage
       */
      callRestartIsReadyUnsubscribeRef.current();
      const unsubscribe = subscribeToMessage({
        messageType: RealtimeMessageType.CallRestartIsReady,
        callback: (_payload, event) => {
          logger.debug(
            '[restartSession/waitForOthersToBeReady] Debug: Person is ready',
            {
              participantId: event.from.id,
            },
          );
          unresolvedIds = unresolvedIds.filter((id) => id !== event.from.id);
          if (unresolvedIds.length === 0) {
            resolve();
            cancelTimeout();
          }
        },
      });

      callRestartIsReadyUnsubscribeRef.current = unsubscribe;
    });
  };

  const restart = async (): Promise<void> => {
    const participantIds = participants
      .map((p) => p.identity?.id)
      // Patient cannot see another patient in realtime so identity will always be undefined.
      .filter((id): id is string => id !== undefined);

    logger.debug('[restartSession/restart] Debug: Initiated', {
      participantIds,
    });
    const waitForOthers = waitForOthersToBeReady(participantIds);

    if (role === RealtimeRole.Provider) {
      await Promise.all(participantIds.map(inviteMemberToRestart));
    }

    try {
      /**
       * Disconnect from the session.
       */
      await disconnectSession();
      logger.debug('[restartSession/restart] Debug: Session disconnected');
      /**
       * Notify others that you are ready to reconnect
       */
      for (const participantId of participantIds) {
        void notifyThatIAmReady(participantId);
      }

      try {
        await restartMedia();
      } catch (error) {
        logger.error('[restartSession/restartMedia]', error);
      }

      await waitForOthers;

      await reconnectSession();

      void transition(CallTransition.INIT_DONE);
      logger.debug('[restartSession/restart] Debug: Restart finalized');
    } catch (error) {
      logger.error('[restartSession/restart]', error);
      throw error;
    }
  };

  return restart;
};
